import { useRef, useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { ResponsiveLine } from "@nivo/line";
import { Box, useToken, keyframes } from "@chakra-ui/react";
import { EPulseChartMessage } from "@components";
import { selectEPulseData } from "@store/selectors/meetingSelectors";
import dayjs from "dayjs";

const pulseAnimation = keyframes`
  0% { opacity: 0.4; }
  50% { opacity: 1; }
  100% { opacity: 0.4; }
`;

const waveAnimation = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const EPulseChartAnimated = ({
  agenda,
  animationType = "heartbeat",
  showJustStartingOverlay = false,
}) => {
  const ref = useRef(null);
  const [success, high, medium, low, textColor] = useToken("colors", [
    "success",
    "green.300",
    "red.300",
    "yellow.300",
    "dark.500",
  ]);

  const ePulseData = useSelector(selectEPulseData);
  const defaultEpulse = [];
  const currentEPulseData = agenda.live ? ePulseData : defaultEpulse;
  const agendaStarted = agenda.startTime && agenda.live;
  const ePulseDataIsShowing = useMemo(
    () => currentEPulseData?.filter(({ pulse }) => pulse !== null).length >= 2,
    [currentEPulseData]
  );

  // Generate animation data
  const [animationData, setAnimationData] = useState([]);
  const [animationFrame, setAnimationFrame] = useState(0);

  useEffect(() => {
    if (!agendaStarted || ePulseDataIsShowing) {
      return;
    }

    const interval = setInterval(() => {
      setAnimationFrame((prev) => prev + 1);
    }, 100);

    return () => clearInterval(interval);
  }, [agendaStarted, ePulseDataIsShowing]);

  useEffect(() => {
    if (!agendaStarted || ePulseDataIsShowing) {
      setAnimationData([]);
      return;
    }

    const startTime = dayjs(agenda.startTime);
    const now = dayjs();
    const duration = now.diff(startTime, "seconds");
    const maxPoints = Math.min(duration * 2, 60);

    if (animationType === "heartbeat") {
      // Generate heartbeat/EKG pattern
      const data = [];
      for (let i = 0; i < maxPoints; i++) {
        const t = (i / 10) * Math.PI;
        let y = 0;

        // Create EKG-like spikes every 10 points
        if (i % 10 === 3) {
          y = 25;
        } else if (i % 10 === 4) {
          y = -10;
        } else if (i % 10 === 5) {
          y = 35;
        } else if (i % 10 === 6) {
          y = -15;
        } else if (i % 10 === 7) {
          y = 5;
        } else {
          y = Math.sin(t) * 2;
        }

        data.push({
          x: startTime.add(i * 500, "milliseconds").toDate(),
          y: y * (0.8 + Math.sin(animationFrame / 20) * 0.2),
        });
      }
      setAnimationData(data);
    } else if (animationType === "baseline") {
      // Generate pulsing baseline
      const data = [];
      const pulse = Math.sin(animationFrame / 15) * 3;

      for (let i = 0; i < 30; i++) {
        const wave = Math.sin(i / 5 + animationFrame / 10) * 2;
        data.push({
          x: startTime.add(i * 1000, "milliseconds").toDate(),
          y: pulse + wave,
        });
      }
      setAnimationData(data);
    } else if (animationType === "warming") {
      // Generate warming up pattern with fake data bouncing around midpoint
      const data = [];
      const timeProgression = Math.min(duration / 30, 1); // 0 to 1 over 30 seconds

      for (let i = 0; i < maxPoints; i++) {
        // Base noise around 0 (midpoint)
        const baseNoise = (Math.random() - 0.5) * 8;

        // Gradual amplitude increase as system "warms up"
        const amplitudeMultiplier = 0.3 + timeProgression * 0.7;

        // Add some periodic movement for more organic feel
        const periodicMovement = Math.sin((animationFrame + i * 2) / 12) * 5;

        // Slight drift towards medium engagement over time
        const drift = Math.sin(i / 8) * 3 * timeProgression;

        const y = (baseNoise + periodicMovement + drift) * amplitudeMultiplier;

        data.push({
          x: startTime.add(i * 500, "milliseconds").toDate(),
          y: Math.max(-45, Math.min(45, y)), // Clamp to chart bounds
        });
      }
      setAnimationData(data);
    }
  }, [
    agendaStarted,
    ePulseDataIsShowing,
    animationType,
    agenda.startTime,
    animationFrame,
  ]);

  const commonProperties = {
    margin: { top: 20, right: 10, bottom: 10, left: 35 },
    animate: true,
    enableSlices: "x",
  };

  const EPulseTick = (tick) => {
    const tickLabels = {
      50: { label: "High", color: high },
      0: { label: "Med", color: medium },
      "-50": { label: "Low", color: low },
    };

    const label = tickLabels[tick.value]?.label;
    const color = tickLabels[tick.value]?.color;

    if (!label) return null;

    return (
      <g transform={`translate(${tick.x - 5},${tick.y})`}>
        <text
          dominantBaseline="middle"
          textAnchor="end"
          transform="translate(-10,0)"
          style={{
            fontSize: "8px",
            fill: textColor,
          }}
        >
          {label}
        </text>
        <circle
          cx="2"
          cy="2"
          r="2"
          fill={color}
          transform="translate(-7, -2)"
        />
      </g>
    );
  };

  // Show animation when meeting started but no data yet
  const showAnimation = agendaStarted && !ePulseDataIsShowing;
  // Show fake data when agenda hasn't started
  const showFakeData = !agendaStarted;

  // Generate static fake data for not started state
  const fakeDataForNotStarted = useMemo(() => {
    const baseTime = dayjs();
    const data = [];

    // Create 8-10 data points with gentle variations around medium engagement
    for (let i = 0; i < 9; i++) {
      const baseValue = -5 + Math.sin(i * 0.8) * 8; // Gentle wave around medium
      const noise = Math.sin(i * 1.3) * 3; // Small variations
      data.push({
        x: baseTime
          .subtract(45, "minutes")
          .add(i * 6, "minutes")
          .toDate(),
        y: baseValue + noise,
      });
    }
    return data;
  }, []);

  const blurStyles = {
    filter: "auto",
    blur: "2px",
  };

  return (
    <>
      {!ePulseDataIsShowing && !showFakeData && showJustStartingOverlay ? (
        <EPulseChartMessage
          agendaStarted={agendaStarted}
          ePulseDataIsShowing={ePulseDataIsShowing}
        />
      ) : null}
      <Box
        ref={ref}
        position="relative"
        zIndex={0}
        height="100px"
        data-testid="epulse-chart"
        {...(!ePulseDataIsShowing && !showFakeData && showJustStartingOverlay
          ? blurStyles
          : null)}
      >
        <ResponsiveLine
          {...commonProperties}
          data={[
            {
              id: "points",
              data: showFakeData
                ? fakeDataForNotStarted
                : showAnimation
                ? animationData
                : currentEPulseData.map((item) => ({
                    x: dayjs(item.time).toDate(),
                    y: item.pulse,
                  })),
            },
          ]}
          curve={
            animationType === "heartbeat" && showAnimation
              ? "linear"
              : "monotoneX"
          }
          colors={showAnimation ? [success + "99"] : [success]}
          yScale={{
            type: "linear",
            stacked: false,
            min: -50,
            max: 50,
          }}
          enableGridX={false}
          isInteractive={false}
          axisBottom={null}
          animate={false}
          pointLabelYOffset={0}
          pointSize={showAnimation ? 0 : 5}
          lineWidth={showAnimation ? 2 : 2}
          enableArea={!showAnimation}
          areaOpacity={0.1}
          areaBaselineValue={-50}
          axisLeft={{
            renderTick: EPulseTick,
          }}
        />
        {showAnimation && animationType === "baseline" && (
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            pointerEvents="none"
            overflow="hidden"
          >
            <Box
              position="absolute"
              top="50%"
              left="0"
              width="100%"
              height="2px"
              bgGradient={`linear(to-r, transparent, ${success}, transparent)`}
              animation={`${waveAnimation} 3s linear infinite`}
              opacity={0.5}
            />
          </Box>
        )}
        {showAnimation && (
          <Box
            position="absolute"
            bottom="10px"
            right="10px"
            fontSize="10px"
            color="gray.500"
            animation={`${pulseAnimation} 2s ease-in-out infinite`}
          >
            {animationType === "heartbeat"
              ? "Monitoring..."
              : animationType === "warming"
              ? "Calibrating..."
              : "Initializing..."}
          </Box>
        )}
      </Box>
    </>
  );
};

export default EPulseChartAnimated;
